import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { useContext, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { ChatContext } from "../../contexts/chat/context";
import type { AgentWorkStepStatus } from "../../contexts/chat/type";
import { CHAT_FONT_STACK } from "../../contexts/material_ui/theme";
import { customerFacingActivity } from "./customer_facing";

const sanitizeStreamedPreview = (raw: string): string => {
  if (!raw) return "";

  return raw
    .split("\n")
    .map((line) => {
      if (/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) return "";
      if (/^\s*```/.test(line)) return "";

      if (line.includes("|") && (line.match(/\|/g) || []).length >= 2) {
        if (/^\s*\|?[\s:-]+(\|[\s:-]+)+\|?\s*$/.test(line)) return "";
        const cells = line
          .split("|")
          .map((cell) => cell.trim())
          .filter(Boolean);
        return cells.length > 0 ? `- ${cells.join(" · ")}` : "";
      }

      return line.replace(/\s{2,}/g, " ").trimEnd();
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const activityScope = (activity: string): string =>
  activity === "Understanding your question"
    ? "Reading the question and current planning context"
    : activity === "Checking the selected cell" ||
        activity === "Tracing contributing data" ||
        activity === "Inspecting demand contributors"
      ? "Checking the selected value and its saved contributors"
      : activity === "Comparing demand in the selected view"
        ? "Comparing the saved Demand periods and groups"
        : activity === "Comparing the current forecast periods"
          ? "Comparing the saved monthly forecast"
          : activity === "Reviewing supply drivers and balances"
            ? "Checking the saved supply equation and material drivers"
            : activity === "Reviewing purchase-order priorities" ||
                activity === "Reviewing purchase-order drivers"
              ? "Checking quantities, timing, constraints, and proposal drivers"
              : activity === "Reviewing inventory risk in the selected view" ||
                  activity === "Reviewing inventory risk and shortages"
                ? "Checking shortage exposure and inventory drivers"
                : activity === "Reviewing customer documentation"
                  ? "Reading the customer guidance available to Nucleus"
                  : activity === "Checking the selected run" ||
                      activity === "Inspecting the selected planning run"
                    ? "Using the selected planning run"
                    : "Turning the available evidence into a planner takeaway";

type VisibleWorkStep = {
  id: string;
  label: string;
  status: AgentWorkStepStatus;
};

const turnPhaseLabels = [
  ["understand", "Understand"],
  ["gather", "Gather"],
  ["analyze", "Analyze"],
  ["answer", "Answer"],
] as const;

export const AgentThinking = () => {
  const { agentState } = useContext(ChatContext);
  const fallbackActivity = useMemo(
    () => customerFacingActivity(agentState.reasoning),
    [agentState.reasoning],
  );
  const workSteps = useMemo<VisibleWorkStep[]>(() => {
    const reported = agentState.workSteps ?? [];
    if (reported.length > 0) {
      const visible: VisibleWorkStep[] = [];
      for (const step of reported) {
        const label = customerFacingActivity(step.label);
        const prior = visible.at(-1);
        if (prior?.label === label) {
          visible[visible.length - 1] = { ...prior, status: step.status };
        } else {
          visible.push({ id: step.id, label, status: step.status });
        }
      }
      return visible.slice(-4);
    }

    const history: VisibleWorkStep[] = [];
    for (const step of agentState.reasoningHistory) {
      const label = customerFacingActivity(step.text);
      if (history.at(-1)?.label !== label && label !== fallbackActivity) {
        history.push({
          id: `history-${step.iteration}`,
          label,
          status: "complete",
        });
      }
    }
    return [
      ...history.slice(-2),
      { id: "current", label: fallbackActivity, status: "active" as const },
    ];
  }, [agentState.reasoningHistory, agentState.workSteps, fallbackActivity]);

  if (agentState.status !== "streaming") return null;

  const streamedText = sanitizeStreamedPreview(agentState.streamedText);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Nucleus response in progress"
      sx={{
        alignSelf: "stretch",
        mx: 0.75,
        my: 0.75,
        px: 1.5,
        py: 1.35,
        overflow: "hidden",
        borderRadius: 2.25,
        border: "1px solid rgba(56, 119, 247, 0.18)",
        bgcolor: "rgba(56, 119, 247, 0.035)",
        fontFamily: CHAT_FONT_STACK,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 11,
            height: 11,
            borderRadius: "50%",
            border: "1.5px solid rgba(56, 119, 247, 0.28)",
            borderTopColor: "#3877F7",
            "@keyframes nucleusWorkSpin": {
              to: { transform: "rotate(360deg)" },
            },
            "@media (prefers-reduced-motion: no-preference)": {
              animation: "nucleusWorkSpin 0.9s linear infinite",
            },
          }}
        />
        <Typography
          sx={{
            flex: 1,
            fontFamily: CHAT_FONT_STACK,
            fontSize: "0.82rem",
            lineHeight: 1.35,
            fontWeight: 700,
            color: "#2C56B0",
          }}
        >
          Working...
        </Typography>
      </Box>

      {agentState.turnPhases && (
        <Box
          role="list"
          aria-label="Nucleus response phases"
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 0.45,
          }}
        >
          {turnPhaseLabels.map(([phase, label]) => {
            const complete = agentState.turnPhases?.[phase] === true;
            return (
              <Box
                key={phase}
                role="listitem"
                data-testid={`nucleus-phase-${phase}`}
                data-complete={complete ? "true" : "false"}
                aria-label={`${label} phase ${complete ? "complete" : "waiting"}`}
                sx={{ minWidth: 0 }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    height: 3,
                    borderRadius: 999,
                    bgcolor: complete ? "#3877F7" : "rgba(102, 116, 138, 0.13)",
                  }}
                />
                <Typography
                  sx={{
                    mt: 0.35,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontFamily: CHAT_FONT_STACK,
                    fontSize: "0.61rem",
                    lineHeight: 1.3,
                    fontWeight: complete ? 700 : 600,
                    color: complete ? "#2C56B0" : "#9AA5B5",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 0.7, mt: 1.05 }}>
        {workSteps.map((step) => (
          <Box key={step.id} sx={{ display: "flex", gap: 0.75 }}>
            <Box
              sx={{
                width: 16,
                height: 18,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              {step.status === "complete" ? (
                <CheckRoundedIcon
                  aria-hidden="true"
                  sx={{ fontSize: 14, color: "success.main" }}
                />
              ) : step.status === "failed" ? (
                <ErrorOutlineRoundedIcon
                  aria-hidden="true"
                  sx={{ fontSize: 14, color: "error.main" }}
                />
              ) : (
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: "#3877F7",
                  }}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: CHAT_FONT_STACK,
                  fontSize: "0.75rem",
                  lineHeight: 1.45,
                  fontWeight: step.status === "active" ? 700 : 600,
                  color:
                    step.status === "failed"
                      ? "error.main"
                      : step.status === "active"
                        ? "#2C56B0"
                        : "text.secondary",
                }}
              >
                {step.label}
              </Typography>
              {step.status === "active" && (
                <Typography
                  sx={{
                    mt: 0.08,
                    fontFamily: CHAT_FONT_STACK,
                    fontSize: "0.7rem",
                    lineHeight: 1.4,
                    color: "text.secondary",
                  }}
                >
                  {activityScope(step.label)}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {streamedText && (
        <Box
          aria-label={
            agentState.answerStarted === false
              ? "Partial response"
              : "Answer being written"
          }
          sx={{
            mt: 1.15,
            pt: 1.1,
            borderTop: "1px solid rgba(56, 119, 247, 0.12)",
            color: "text.primary",
            "& p": { m: 0, mb: 0.75, "&:last-child": { mb: 0 } },
            "& ul, & ol": { mt: 0.5, mb: 0.75, pl: 2.5 },
            "& li": { mb: 0.35 },
            "& h1, & h2, & h3, & h4": {
              mt: 1,
              mb: 0.5,
              fontSize: "0.86rem",
              lineHeight: 1.35,
              fontWeight: 700,
            },
            "& code": {
              px: 0.4,
              py: 0.1,
              borderRadius: 0.75,
              bgcolor: "rgba(16, 17, 19, 0.06)",
              fontSize: "0.85em",
            },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => (
                <Typography
                  component="p"
                  sx={{
                    fontFamily: CHAT_FONT_STACK,
                    fontSize: "0.84rem",
                    lineHeight: 1.58,
                  }}
                >
                  {children}
                </Typography>
              ),
              a: ({ children }) => <span>{children}</span>,
              img: ({ alt }) => (
                <span>{alt?.trim() ? alt : "Image omitted"}</span>
              ),
            }}
          >
            {streamedText}
          </ReactMarkdown>
        </Box>
      )}
    </Box>
  );
};
